import { useState } from 'react'
import useSWR from 'swr'
import { API_BASE_URL } from 'config/api'
import { fetcher, IError, ApiResponse, Meta } from 'utils/fetchers'
import { ProductImage } from 'utils/imageUtils'

const apiUrl = `${API_BASE_URL}/products`

interface Attributes {
  appearance: string
  author: string
  pagesNumber: string
  publishYear: string
  translator: string
}

interface Product {
  attributes: Attributes
  price: number
  title: string
  vendor: string
  barcode: string
  compareAtPrice: number | null
  createdAt: Date
  description: string
  id: string
  images: ProductImage[]
  orderId: string | null
  ownerCellphone: string
  ownerId: string
  ownerLastName: string
}

interface PaginationOptions {
  page?: number
  per_page?: number
}

export function useFetchProducts(
  categoryId?: string | null,
  paginationOptions?: PaginationOptions,
) {
  const [page, setPage] = useState(paginationOptions?.page || 1)
  const [per_page, setPerPage] = useState(paginationOptions?.per_page || 20)

  // Build URL with query parameters
  const params = new URLSearchParams({
    page: page.toString(),
    per_page: per_page.toString(),
  })

  // Add categoryId only if it exists
  if (categoryId) {
    params.append('categoryId', categoryId)
  }

  const url = `${apiUrl}?${params.toString()}`

  const { data: response, error } = useSWR<ApiResponse<Product[]>, IError | null>(
    url,
    fetcher,
  )

  // Extract data and meta from response
  const products = response?.data || undefined
  const meta = response?.meta

  // Check for API-level errors
  const apiError = response && !response.success ? response.error : null
  const finalError = error || apiError

  const goToPage = (pageNum: number) => setPage(Math.max(1, pageNum))

  const setItemsPerPage = (count: number) => {
    setPerPage(Math.max(1, count))
    setPage(1) // Reset to first page when changing items per page
  }

  return {
    products,
    isLoading: !finalError && !response,
    isError: !!finalError,
    error: finalError,
    meta: meta as Meta | undefined,
    pagination: {
      page,
      per_page,
      goToPage,
      setItemsPerPage,
    },
  }
}
